'use client';

import { useMemo, useState } from 'react';

import {
  FilterableIndexItemListType,
  FilterableIndexItemListVariant,
} from '@/app/(main)/(pages)/(institucional)/(index)/_enums/filterable-index-item-list.enum';
import { SeoExamCityCard } from '@/app/(main)/(pages)/(institucional)/(index)/exames/por-exame/[slug]/_components/seo-exam-city-card.component';
import { SeoExamUfCard } from '@/app/(main)/(pages)/(institucional)/(index)/exames/por-exame/[slug]/[uf]/_components/seo-exam-uf-card.component';
import { SpecialtyCityCard } from '@/app/(main)/(pages)/(institucional)/(index)/especialidades/por-especialidade/[slug]/_components/specialty-city-card.component';
import { SpecialtyUfCard } from '@/app/(main)/(pages)/(institucional)/(index)/especialidades/por-especialidade/[slug]/[uf]/_components/specialty-uf-card.component';
import { ClientListFilterInput } from '@/components/ui/list-filter-input/client-list-filter-input.component';
import type { IndexItem } from '@/types/index/index-list.type';

export interface FilterableIndexItemListProps {
  items: IndexItem[];
  placeholder?: string;
  variant: FilterableIndexItemListVariant;
  type: FilterableIndexItemListType;
}

function filterIndexItems(
  items: IndexItem[],
  term: string,
  variant: FilterableIndexItemListVariant,
): IndexItem[] {
  const t = term.trim().toLowerCase();
  if (!t) {
    return items;
  }
  return items.filter((item) => {
    if (!item) {
      return false;
    }
    const inKey = item.key?.toLowerCase().includes(t);
    const inName = item.name?.toLowerCase().includes(t);
    if (variant === FilterableIndexItemListVariant.CITY) {
      return Boolean(inKey || inName);
    }
    const inCities = item.cities?.some(
      (city) =>
        city?.city?.toLowerCase().includes(t)
        || city?.slug?.toLowerCase().includes(t)
        || city?.state_abbr?.toLowerCase().includes(t),
    );
    return Boolean(inKey || inName || inCities);
  });
}

export function FilterableIndexItemList({
  items,
  placeholder = 'Filtrar',
  variant,
  type,
}: FilterableIndexItemListProps) {
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => filterIndexItems(items, filter, variant), [items, filter, variant]);

  return (
    <>
      <div className="px-5 md:px-0 mb-8">
        <ClientListFilterInput
          value={filter}
          onChange={setFilter}
          placeholder={placeholder}
          className="max-w-md"
        />
      </div>
      {filtered.length > 0
        ? (
            variant === FilterableIndexItemListVariant.CITY
              ? type === FilterableIndexItemListType.SPECIALTY
                ? filtered.map((item, index) => (
                    <SpecialtyCityCard
                      key={item.key}
                      specialty={item}
                      isFirst={index === 0}
                    />
                  ))
                : filtered.map((item, index) => (
                    <SeoExamCityCard
                      key={item.key}
                      exam={item}
                      isFirst={index === 0}
                    />
                  ))
              : type === FilterableIndexItemListType.SPECIALTY
                ? filtered.map((item, index) => (
                    <SpecialtyUfCard
                      key={`key-specialty${item.key}`}
                      specialty={item}
                      filterTerm={filter}
                      isFirst={index === 0}
                    />
                  ))
                : filtered.map((item, index) => (
                    <SeoExamUfCard
                      key={`key-exam${item.key}`}
                      exam={item}
                      filterTerm={filter}
                      isFirst={index === 0}
                    />
                  ))
          )
        : (
            <p className="px-5 md:px-0 text-gray-600" role="status">
              {variant === FilterableIndexItemListVariant.CITY
                ? 'Nenhum estado encontrado para o filtro informado.'
                : 'Nenhuma cidade encontrada para o filtro informado.'}
            </p>
          )}
    </>
  );
}
